/* global globalThis */
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';
import { Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Spinner } from 'react-bootstrap';
import ChartContainer from './ChartContainer';
import { chartTypeEnum } from '../constants';
import ErrorComponent from './ErrorComponent';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import DownloadIcon from '@mui/icons-material/Download';
import EnlargedChartModal from '../EnlargedChartModal';
import NoDataComponent from './NoDataComponent';
import ChartContent from './ChartContent';
import downloadData from '../../../utils/AnalyticsExportUtils';
import CalendarIconDatePicker from '../../../shared/UIComponents/CalendarIconDatePicker';
import dayjs from '../../../utils/dayjs';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import EventIcon from '@mui/icons-material/Event';
import html2canvas from 'html2canvas';
import AnalyticsCardHeader from './AnalyticsCardHeader';
import InfoPopover from './InfoPopover';
import { normalizeAnalyticsEntityTitleCase } from '../analyticsTextUtils';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

const useStyles = makeStyles(() => ({
  tableContainer: {
    maxHeight: '100vh',
    height: '100%',
    overflow: 'scroll',
    border: 'solid black',
    borderWidth: '1px',
    borderRadius: '1px',
  },
  text: {
    paddingLeft: '5%',
    paddingTop: '1vh',
    color: '#ffffff',
    fontSize: '100%',
  },
  tableHeader: {
    backgroundColor: '#002145',
    background: '#002145',
    height: '5vh',
    textAlign: 'left',
  },
  gridItem: {
    position: 'absolute',
  },
  content: {
    position: 'relative',
  },
  analyticsCardBody: {
    minHeight: '280px',
    flex: '1 1 auto',
    height: '100%',
    padding: '0 16px 16px',
    boxSizing: 'border-box',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const GLOBAL_SCOPE = (() => {
  if (typeof globalThis === 'object') {
    return globalThis;
  }
  if (typeof document === 'object') {
    return document.defaultView;
  }
  return undefined;
})();

const buildHeaderActions = ({
  isTeamAnalyticsCard,
  isYearlyChart,
  openCalendarHandler,
  hasInfo,
  openInfoHandler,
  canDownloadData,
  chartData,
  type,
  title,
  downloadChartImage,
  openButtonHandler,
}) => {
  const actions = [];

  if (isTeamAnalyticsCard) {
    if (isYearlyChart) {
      actions.push({
        key: 'calendar',
        tooltip: 'Choose year',
        ariaLabel: 'Choose year',
        onClick: openCalendarHandler,
        icon: <EventIcon />,
      });
    } else {
      actions.push({ key: 'calendar-placeholder', placeholder: true });
    }
  }

  if (hasInfo) {
    actions.push({
      key: 'info',
      tooltip: 'How this chart is counted',
      ariaLabel: 'How this chart is counted',
      onClick: openInfoHandler,
      icon: <InfoOutlinedIcon />,
    });
  }

  if (canDownloadData) {
    actions.push({
      key: 'download',
      tooltip: 'Download data',
      ariaLabel: 'Download data',
      onClick: () => downloadData(chartData, type, title),
      icon: <DownloadIcon />,
    });
  }

  actions.push(
    {
      key: 'camera',
      tooltip: 'Download chart image',
      ariaLabel: 'Download chart image',
      onClick: downloadChartImage,
      icon: <PhotoCameraIcon />,
    },
    {
      key: 'expand',
      tooltip: 'Expand chart',
      ariaLabel: 'Expand chart',
      onClick: openButtonHandler,
      icon: <FullscreenIcon />,
    }
  );

  return actions;
};

const renderEnlargedChartModal = ({
  open,
  setOpen,
  enlargedViewRequest,
  chartDataIn,
  enlargedViewData,
  chartData,
  isYearlyChart,
  dateQuery,
  query,
}) => (
  <EnlargedChartModal
    open={open}
    onClose={() => setOpen(false)}
    display={
      enlargedViewRequest ? (
        <ChartContent
          chartDataParam={{
            ...chartDataIn,
            networkRequest: enlargedViewRequest,
            enlargedViewRequest: enlargedViewData,
            chartData: enlargedViewData,
          }}
          query={isYearlyChart ? dateQuery : query}
          open={open}
          showTitle={false}
        ></ChartContent>
      ) : (
        <ChartContent
          chartDataParam={{ ...chartDataIn, chartData: chartData }}
          query={isYearlyChart ? dateQuery : query}
          open={open}
          showTitle={false}
        ></ChartContent>
      )
    }
  ></EnlargedChartModal>
);

const ChartCardContent = ({
  title,
  normalizedSubTitle,
  badges,
  sectionLabel,
  showSectionAccent,
  headerActions,
  isTeamAnalyticsCard,
  openCalendarPopover,
  closeCalendarHandler,
  date,
  changeYearFromPopover,
  calendarAnchorEl,
  hasInfo,
  openInfoPopover,
  infoAnchorEl,
  closeInfoHandler,
  infoHeading,
  normalizedScope,
  infoItems,
  classes,
  chartCaptureRef,
  chartDataIn,
  chartData,
  isYearlyChart,
  dateQuery,
  query,
  changeYear,
}) => (
  <>
    <AnalyticsCardHeader
      title={title}
      subTitle={normalizedSubTitle}
      badges={badges}
      sectionLabel={sectionLabel}
      showSectionAccent={showSectionAccent}
      actions={headerActions}
      actionLayoutVariant={isTeamAnalyticsCard ? 'team' : 'default'}
    />
    {isTeamAnalyticsCard && (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          open={openCalendarPopover}
          views={['year']}
          label="Year"
          onClose={closeCalendarHandler}
          value={date}
          onChange={changeYearFromPopover}
          slotProps={{
            popper: {
              anchorEl: calendarAnchorEl,
              placement: 'bottom-start',
            },
            textField: {
              InputProps: { style: { display: 'none' } },
            },
          }}
        />
      </LocalizationProvider>
    )}
    {hasInfo && (
      <InfoPopover
        open={openInfoPopover}
        anchorEl={infoAnchorEl}
        onClose={closeInfoHandler}
        heading={infoHeading}
        scope={normalizedScope}
        items={infoItems}
      />
    )}
    <div className={`${classes.analyticsCardBody} analyticsCardBody`} ref={chartCaptureRef}>
      <ChartContent
        className={classes.content}
        chartDataParam={{ ...chartDataIn, chartData: chartData }}
        query={isYearlyChart ? dateQuery : query}
        showCameraButton={false}
        showTitle={false}
      ></ChartContent>
    </div>
    {!isTeamAnalyticsCard && isYearlyChart && (
      <CalendarIconDatePicker value={date} onChange={(newYear) => changeYear(newYear)} title={title} />
    )}
  </>
);

ChartCardContent.propTypes = {
  title: PropTypes.string,
  normalizedSubTitle: PropTypes.string,
  badges: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        label: PropTypes.string,
        variant: PropTypes.string,
        tooltip: PropTypes.string,
      }),
    ])
  ),
  sectionLabel: PropTypes.string,
  showSectionAccent: PropTypes.bool,
  headerActions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      tooltip: PropTypes.string,
      ariaLabel: PropTypes.string,
      onClick: PropTypes.func,
      icon: PropTypes.element,
      disabled: PropTypes.bool,
      placeholder: PropTypes.bool,
    })
  ),
  isTeamAnalyticsCard: PropTypes.bool,
  openCalendarPopover: PropTypes.bool,
  closeCalendarHandler: PropTypes.func,
  date: PropTypes.any,
  changeYearFromPopover: PropTypes.func,
  calendarAnchorEl: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  hasInfo: PropTypes.bool,
  openInfoPopover: PropTypes.bool,
  infoAnchorEl: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  closeInfoHandler: PropTypes.func,
  infoHeading: PropTypes.string,
  normalizedScope: PropTypes.string,
  infoItems: PropTypes.arrayOf(PropTypes.string),
  classes: PropTypes.shape({
    analyticsCardBody: PropTypes.string,
    content: PropTypes.string,
  }),
  chartCaptureRef: PropTypes.shape({ current: PropTypes.any }),
  chartDataIn: PropTypes.shape({
    title: PropTypes.string,
    type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    networkRequest: PropTypes.func,
    enlargedViewRequest: PropTypes.func,
    isYearlyChart: PropTypes.bool,
    queryKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    subTitle: PropTypes.string,
    scope: PropTypes.string,
    infoTitle: PropTypes.string,
    infoBody: PropTypes.arrayOf(PropTypes.string),
    sectionLabel: PropTypes.string,
    showSectionAccent: PropTypes.bool,
    analyticsVariant: PropTypes.string,
    category: PropTypes.string,
    addOn: PropTypes.string,
  }),
  chartData: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.array]),
  isYearlyChart: PropTypes.bool,
  dateQuery: PropTypes.string,
  query: PropTypes.string,
  changeYear: PropTypes.func,
};

function ChartComponent({ chartDataIn, query }) {
  //component for rending a chart/numbox
  const {
    title,
    type,
    networkRequest,
    enlargedViewRequest,
    isYearlyChart,
    queryKey,
    subTitle,
    scope,
    infoTitle,
    infoBody,
    badges,
    sectionLabel,
    showSectionAccent,
  } = chartDataIn;
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [infoAnchorEl, setInfoAnchorEl] = useState(null);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
  const chartCaptureRef = useRef(null);
  const infoPopoverInstanceIdRef = useRef(`chart-info-popover-${Math.random().toString(36).slice(2)}`);
  const isTeamAnalyticsCard = chartDataIn.analyticsVariant === 'team';

  const now = dayjs().endOf('day');
  const defaultDateQuery = `year=${now.format('YYYY')}`;

  const [dateQuery, setDateQuery] = useState(defaultDateQuery);
  const [date, setDate] = useState(now);

  const changeYear = (newDate) => {
    setDate(newDate);
    setDateQuery(`year=${newDate.format('YYYY')}`);
  };

  const supportedDataExports = new Set([
    chartTypeEnum.ChartJSBar,
    chartTypeEnum.ChartJSDoughnut,
    chartTypeEnum.ChartJSStackedBar,
    chartTypeEnum.line,
    chartTypeEnum.PlotlyMonthLine,
    chartTypeEnum.topHits,
    chartTypeEnum.wordCloud,
    chartTypeEnum.numberBox,
  ]);

  const resolvedQuery = isYearlyChart ? dateQuery : query;
  const baseQueryKey = queryKey ?? title;

  const chartQuery = useQuery({
    queryKey: ['analytics-chart', baseQueryKey, isYearlyChart ? 'year' : 'range', resolvedQuery],
    queryFn: () => networkRequest(resolvedQuery),
    enabled: Boolean(resolvedQuery),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });

  const enlargedViewQuery = useQuery({
    queryKey: ['analytics-chart', `${baseQueryKey}-enlarged`, isYearlyChart ? 'year' : 'range', resolvedQuery],
    queryFn: () => enlargedViewRequest(resolvedQuery),
    enabled: Boolean(enlargedViewRequest) && Boolean(resolvedQuery),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });

  const chartData = chartQuery.isLoading ? 'loading' : chartQuery.data ?? 'loading';
  const enlargedViewData = enlargedViewQuery.isLoading ? 'loading' : enlargedViewQuery.data ?? 'loading';
  const normalizedSubTitle = normalizeAnalyticsEntityTitleCase(subTitle);
  const normalizedScope = normalizeAnalyticsEntityTitleCase(
    typeof scope === 'string' ? scope.trim().replace(/^included:\s*/i, '') : ''
  );
  const infoItems = Array.isArray(infoBody)
    ? infoBody
        .filter((item) => typeof item === 'string' && item.trim())
        .map((item) => normalizeAnalyticsEntityTitleCase(item))
    : [];
  const hasInfo = Boolean(normalizedScope || infoItems.length > 0);
  const infoHeading = normalizeAnalyticsEntityTitleCase(infoTitle || 'How this chart is counted');
  const openInfoPopover = Boolean(infoAnchorEl);
  const openCalendarPopover = Boolean(calendarAnchorEl);

  const openButtonHandler = () => {
    setOpen(true);
  };
  const openInfoHandler = (event) => {
    const clickedEl = event.currentTarget;
    // Toggle if clicking the same icon again.
    if (infoAnchorEl === clickedEl) {
      setInfoAnchorEl(null);
      return;
    }
    // Notify sibling chart cards so only one popover is open across the grid.
    GLOBAL_SCOPE?.dispatchEvent?.(
      new CustomEvent('custom-analytics-info-open', {
        detail: { instanceId: infoPopoverInstanceIdRef.current },
      })
    );
    setInfoAnchorEl(clickedEl);
  };
  const closeInfoHandler = () => {
    setInfoAnchorEl(null);
  };
  const openCalendarHandler = (event) => {
    setCalendarAnchorEl(event.currentTarget);
  };
  const closeCalendarHandler = () => {
    setCalendarAnchorEl(null);
  };
  const changeYearFromPopover = (newDate) => {
    if (!newDate) {
      return;
    }
    changeYear(newDate);
    closeCalendarHandler();
  };

  useEffect(() => {
    const closeWhenAnotherChartOpens = (event) => {
      if (event?.detail?.instanceId !== infoPopoverInstanceIdRef.current) {
        setInfoAnchorEl(null);
      }
    };

    GLOBAL_SCOPE?.addEventListener?.('custom-analytics-info-open', closeWhenAnotherChartOpens);
    return () => {
      GLOBAL_SCOPE?.removeEventListener?.('custom-analytics-info-open', closeWhenAnotherChartOpens);
    };
  }, []);
  const downloadChartImage = async () => {
    if (!chartCaptureRef.current) {
      return;
    }

    const canvas = await html2canvas(chartCaptureRef.current, {});
    const data = canvas.toDataURL('image/jpg');
    const link = document.createElement('a');

    if (typeof link.download === 'string') {
      link.href = data;
      link.download = 'image.jpg';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } else {
      GLOBAL_SCOPE?.open?.(data);
    }
  };
  const canDownloadData = supportedDataExports.has(type) && !isYearlyChart;
  const headerActions = buildHeaderActions({
    isTeamAnalyticsCard,
    isYearlyChart,
    openCalendarHandler,
    hasInfo,
    openInfoHandler,
    canDownloadData,
    chartData,
    type,
    title,
    downloadChartImage,
    openButtonHandler,
  });

  if (chartQuery.isError) {
    return <ErrorComponent message={chartQuery.error?.message ?? 'Failed to load chart data.'}></ErrorComponent>;
  }
  if (chartData?.error === 1) {
    return <ErrorComponent message={chartData.message}></ErrorComponent>;
  }
  if (chartData === 'noData') {
    return <NoDataComponent title={title}></NoDataComponent>;
  }
  if (chartData === 'loading') {
    return (
      <ChartContainer
        small={type === chartTypeEnum.numberBox || type === chartTypeEnum.topHits}
        large={type === chartTypeEnum.barWide}
      >
        <div>
          <Typography>Waiting for data</Typography>
          <Spinner animation="border" />
        </div>
      </ChartContainer>
    );
  }

  return (
    <>
      {renderEnlargedChartModal({
        open,
        setOpen,
        enlargedViewRequest,
        chartDataIn,
        enlargedViewData,
        chartData,
        isYearlyChart,
        dateQuery,
        query,
      })}
      <ChartContainer
        small={type === chartTypeEnum.numberBox || type === chartTypeEnum.topHits}
        large={type === chartTypeEnum.barWide}
        exportMeta={{
          queryKey: baseQueryKey,
          title,
          subTitle: normalizedSubTitle,
          category: chartDataIn.category,
          chartType: type,
        }}
      >
        <ChartCardContent
          title={title}
          normalizedSubTitle={normalizedSubTitle}
          badges={badges}
          sectionLabel={sectionLabel}
          showSectionAccent={showSectionAccent}
          headerActions={headerActions}
          isTeamAnalyticsCard={isTeamAnalyticsCard}
          openCalendarPopover={openCalendarPopover}
          closeCalendarHandler={closeCalendarHandler}
          date={date}
          changeYearFromPopover={changeYearFromPopover}
          calendarAnchorEl={calendarAnchorEl}
          hasInfo={hasInfo}
          openInfoPopover={openInfoPopover}
          infoAnchorEl={infoAnchorEl}
          closeInfoHandler={closeInfoHandler}
          infoHeading={infoHeading}
          normalizedScope={normalizedScope}
          infoItems={infoItems}
          classes={classes}
          chartCaptureRef={chartCaptureRef}
          chartDataIn={chartDataIn}
          chartData={chartData}
          isYearlyChart={isYearlyChart}
          dateQuery={dateQuery}
          query={query}
          changeYear={changeYear}
        />
      </ChartContainer>
    </>
  );
}

ChartComponent.propTypes = {
  chartDataIn: PropTypes.shape({
    title: PropTypes.string.isRequired,
    type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    networkRequest: PropTypes.func.isRequired,
    enlargedViewRequest: PropTypes.func,
    isYearlyChart: PropTypes.bool,
    queryKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    subTitle: PropTypes.string,
    scope: PropTypes.string,
    infoTitle: PropTypes.string,
    infoBody: PropTypes.arrayOf(PropTypes.string),
    badges: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          label: PropTypes.string,
          variant: PropTypes.string,
          tooltip: PropTypes.string,
        }),
      ])
    ),
    sectionLabel: PropTypes.string,
    showSectionAccent: PropTypes.bool,
    analyticsVariant: PropTypes.string,
    category: PropTypes.string,
    addOn: PropTypes.string,
  }).isRequired,
  query: PropTypes.string,
};

export default ChartComponent;
